import { useCallback, useEffect, useMemo, useState } from 'react';
import { CPDService } from '../../../../services/cpd.service';
import { useSession } from '../../../../hooks/use-session.hook';
import { usePermissions } from '../../../../hooks/use-permissions.hook';
import { PROFILES } from '../../../../constants';
import { AlertService } from '../../../../services/alert.service';
import {
  FilterDef,
  FilterOption,
  FilterValues
} from '../../../../components/chip-filters/chip-filters.types';
import useRequest from '../../../../hooks/use-request.hook';
import useBgPages from '../../../../hooks/use-bg-pages.hook';
import Icon from '../../../../components/forms/icon.component';
import Aside from '../../../../components/layout/asaid.component';
import Header from '../../../../components/layout/header.component';
import Footer from '../../../../components/layout/footer.component';
import FilterBar from '../../../../components/chip-filters/filter-bar.component';
import ModuleStatsHeader from '../../../../components/cards/header-stats.component';
import TableCPDOperators from './table-operators';
import CpdEntityModal from '../shared/cpd-entity.modal';
import CpdReloadButton from '../shared/cpd-reload.button';
import CpdResolvePreviewModal from '../shared/cpd-resolve-preview.modal';

const CPDOperators: React.FC = () => {
  useBgPages();
  const { session } = useSession();
  const { call, isRequesting } = useRequest();
  const { isAdmin, can } = usePermissions();
  const canWrite = can('cpd.write');
  const canReload = can('cpd.reload');
  const isRoot = session?.profile === PROFILES.SUPER_ADMIN;

  const [operatorsRaw, setOperatorsRaw] = useState<any[]>([]);
  const [filters, setFilters] = useState<FilterValues>({});
  const [showModal, setShowModal] = useState<{ show: boolean; data: any; duplicate: boolean }>({
    show: false,
    data: null,
    duplicate: false
  });
  const [preview, setPreview] = useState<{ show: boolean; initial?: any }>({ show: false });

  const operatorRows = useMemo<any[]>(() => {
    if (!Array.isArray(operatorsRaw)) return [];
    return operatorsRaw.map((item: any) => ({
      id: item.id,
      operatorId: item.operatorId ?? '—',
      enabled: item.enabled ?? false,
      templateId: item.templateId ?? '',
      overrides: item.overrides ?? {},
      createdAt: item.createdAt ?? '',
      updatedAt: item.updatedAt ?? '',
      description: item.description ?? '',
      company: item.operatorId ?? '—',
      channel: 'OPERATOR',
      raw: item
    }));
  }, [operatorsRaw]);

  const operatorOptions = useMemo<FilterOption[]>(() => {
    const seen = new Set<string>();
    return operatorRows.reduce<FilterOption[]>((acc, r) => {
      if (r.operatorId && !seen.has(r.operatorId)) {
        seen.add(r.operatorId);
        acc.push({ value: r.operatorId, label: r.operatorId.toUpperCase() });
      }
      return acc;
    }, []);
  }, [operatorRows]);

  const statusOptions = useMemo<FilterOption[]>(
    () => [
      { value: 'true', label: 'ACTIVO' },
      { value: 'false', label: 'INACTIVO' }
    ],
    []
  );

  const filterDefs = useMemo<FilterDef[]>(() => {
    const defs: FilterDef[] = [];
    if (operatorOptions.length > 0) {
      defs.push({
        key: 'company',
        label: 'Operador / Cliente',
        icon: <Icon icon="ki-office-bag" size="fs-7" />,
        options: operatorOptions,
        multi: false,
        searchable: true
      });
    }
    defs.push({
      key: 'status',
      label: 'Estado',
      icon: <Icon icon="ki-shield-search" size="fs-7" />,
      options: statusOptions,
      multi: false,
      searchable: false
    });
    return defs;
  }, [operatorOptions, statusOptions]);

  const displayRows = useMemo(() => {
    let rows = operatorRows;
    if (filters.company) rows = rows.filter((r) => r.operatorId === filters.company);
    if (filters.status) {
      const target = filters.status === 'true';
      rows = rows.filter((r) => r.enabled === target);
    }
    return [...rows].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }, [operatorRows, filters]);

  const handleFilterChange = useCallback((next: FilterValues) => setFilters(next), []);

  const fetchOperators = useCallback(async () => {
    try {
      const response = await call(() => CPDService.getOperators());
      setOperatorsRaw(response);
    } catch (error: any) {
      AlertService.toastError(error?.error || error);
    }
  }, [call]);

  const handleDeleteOperator = useCallback(
    async (id: string | number) => {
      try {
        const deletePromise = CPDService.deleteOperators(String(id));
        await AlertService.toastLoading(
          deletePromise,
          'Eliminando operador...',
          'Operador eliminado exitosamente.',
          'Ocurrió un error al intentar eliminar el operador.'
        );
        fetchOperators();
      } catch (error: any) {
        AlertService.toastError(error?.error || error);
      }
    },
    [fetchOperators]
  );

  /** Habilitar/deshabilitar sin abrir el modal: PUT con el documento completo. */
  const handleToggleEnabled = useCallback(
    async (row: any) => {
      const next = !row.enabled;
      const ok = await AlertService.question(
        next ? 'Habilitar operador' : 'Deshabilitar operador',
        `"${row.operatorId}" quedará ${next ? 'ACTIVO' : 'INACTIVO'}. ${
          next
            ? 'Su política volverá a aplicarse.'
            : 'Su capa se ignorará al resolver la política (no se borra).'
        }`,
        () => Promise.resolve(true),
        'Continuar'
      );
      if (!ok) return;
      try {
        const it = row.raw;
        const p = CPDService.updateOperator(String(row.id), {
          operatorId: it.operatorId,
          description: it.description ?? undefined,
          enabled: next,
          templateId: it.templateId ?? null,
          overrides: it.overrides ?? {}
        });
        await AlertService.toastLoading(
          p,
          'Actualizando...',
          'Estado actualizado.',
          'No se pudo actualizar el estado.'
        );
        fetchOperators();
      } catch (error: any) {
        AlertService.toastError(error?.error || error);
      }
    },
    [fetchOperators]
  );

  const handleCloseModal = useCallback(
    async (refresh: boolean) => {
      if (refresh) await fetchOperators();
      setShowModal({ show: false, data: null, duplicate: false });
    },
    [fetchOperators]
  );

  const handleRefresh = useCallback(async () => {
    await fetchOperators();
    AlertService.toastSuccess('¡Éxito!, información actualizada correctamente.');
  }, [fetchOperators]);

  useEffect(() => {
    fetchOperators();
  }, [fetchOperators]);

  return (
    <div className="page d-flex flex-row flex-column-fluid">
      <Aside />
      <div className="wrapper d-flex flex-column flex-row-fluid" id="kt_wrapper">
        <Header />
        <div
          className="content fs-6 d-flex flex-column flex-column-fluid bg-cards-p"
          id="kt_content"
        >
          <div className="post fs-6 d-flex flex-column-fluid" id="kt_post">
            <div className="container-fluid px-4 px-md-10 px-xxl-15">
              <ModuleStatsHeader
                stats={[
                  {
                    label: 'Total Operadores',
                    value: displayRows.length,
                    icon: 'ki-duotone ki-abstract-26 fs-1 text-primary',
                    bgColor: 'bg-light-primary',
                    textColor: 'text-primary'
                  },
                  {
                    label: 'Operadores Activos',
                    value: displayRows.filter((r) => r.enabled).length,
                    icon: 'ki-duotone ki-check-circle fs-1 text-primary',
                    bgColor: 'bg-light-primary',
                    textColor: 'text-primary'
                  }
                ]}
                onRefresh={handleRefresh}
                isLoading={isRequesting}
                extraActions={
                  <div className="d-flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="btn btn-light-primary d-flex align-items-center gap-2 hover-scale fs-7 fw-bold rounded-pill"
                      onClick={() => setPreview({ show: true })}
                      title="GET /v1/cpd/resolve"
                    >
                      <Icon icon="ki-magnifier" size="fs-3" />
                      Ver política efectiva
                    </button>
                    {canReload && <CpdReloadButton />}
                    {canWrite && (
                      <button
                        type="button"
                        className="btn btn-primary d-flex align-items-center gap-2 hover-scale fs-7 fw-bold rounded-pill"
                        onClick={() => setShowModal({ show: true, data: null, duplicate: false })}
                      >
                        <Icon icon="ki-plus" size="fs-3" />
                        Agregar Operador
                      </button>
                    )}
                  </div>
                }
              />
              {operatorRows.length > 0 && (
                <FilterBar
                  className="mt-4"
                  defs={filterDefs}
                  value={filters}
                  onChange={handleFilterChange}
                  channelOptions={[]}
                  selectedChannel=""
                  onChannelChange={() => {}}
                />
              )}
              <div className="card shadow-sm border-0 rounded-4 mt-4 mt-md-6 overflow-hidden">
                <div className="card-body p-4 p-md-6">
                  <TableCPDOperators
                    rows={displayRows}
                    isLoading={isRequesting}
                    isRoot={isRoot}
                    isAdmin={isAdmin}
                    canWrite={canWrite}
                    onDelete={handleDeleteOperator}
                    onEdit={(row) => setShowModal({ show: true, data: row, duplicate: false })}
                    onDuplicate={(row) => setShowModal({ show: true, data: row, duplicate: true })}
                    onToggleEnabled={handleToggleEnabled}
                    onPreview={(row) =>
                      setPreview({ show: true, initial: { operatorId: row.operatorId } })
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>

      {showModal.show && (
        <CpdEntityModal
          kind="operator"
          show={showModal.show}
          payload={showModal.data}
          duplicate={showModal.duplicate}
          onRequestClose={handleCloseModal}
        />
      )}
      {preview.show && (
        <CpdResolvePreviewModal
          show={preview.show}
          initial={preview.initial}
          onRequestClose={() => setPreview({ show: false })}
        />
      )}
    </div>
  );
};

export default CPDOperators;
